import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Logo } from '../Logo.tsx';
import type { SiteConfig } from '../../siteContentTypes.ts';

export function LandingHeader({ config }: { config: SiteConfig }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 12);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const visibleLinks = config.header.links.filter((l) => l.visible).sort((a, b) => a.order - b.order);
  const sticky = config.header.sticky;

  return (
    <header
      style={{
        position: sticky ? 'sticky' : 'static',
        top: 0,
        zIndex: 40,
        background: scrolled || !config.header.transparent ? 'rgba(250,250,248,0.85)' : 'transparent',
        backdropFilter: scrolled ? 'blur(10px)' : 'none',
        borderBottom: scrolled ? '1px solid var(--border)' : '1px solid transparent',
        transition: 'all 0.25s ease',
      }}
    >
      <div
        style={{
          maxWidth: 1160,
          margin: '0 auto',
          padding: scrolled ? '10px 24px' : '16px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          transition: 'padding 0.25s ease',
        }}
      >
        <Logo size={scrolled ? 26 : 30} />

        <nav style={{ display: 'flex', gap: 24 }} className="landing-nav-desktop">
          {visibleLinks.map((link) => (
            <a
              key={link.url}
              href={link.url}
              style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', textDecoration: 'none' }}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }} className="landing-header-cta">
          <Link to={config.header.ctaSecondaryUrl} className="btn btn-secondary" style={{ width: 'auto', padding: '9px 16px' }}>
            {config.header.ctaSecondaryText}
          </Link>
          <Link to={config.header.ctaPrimaryUrl} className="btn btn-primary" style={{ width: 'auto', padding: '9px 16px' }}>
            {config.header.ctaPrimaryText}
          </Link>
        </div>

        <button
          type="button"
          className="landing-hamburger"
          aria-label="Menu"
          onClick={() => setMobileOpen((v) => !v)}
          style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileOpen && (
        <div style={{ padding: '0 24px 20px', display: 'flex', flexDirection: 'column', gap: 14 }} className="landing-mobile-menu">
          {visibleLinks.map((link) => (
            <a key={link.url} href={link.url} onClick={() => setMobileOpen(false)} style={{ fontSize: 15, fontWeight: 600, textDecoration: 'none', color: 'var(--text)' }}>
              {link.label}
            </a>
          ))}
          <Link to={config.header.ctaSecondaryUrl} className="btn btn-secondary">{config.header.ctaSecondaryText}</Link>
          <Link to={config.header.ctaPrimaryUrl} className="btn btn-primary">{config.header.ctaPrimaryText}</Link>
        </div>
      )}
    </header>
  );
}
